import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button.js";
import { getSession } from "@/lib/session.js";
import {
  loadCustomCss,
  saveCustomCss,
  deleteCustomCss,
  applyCustomCss,
  validate,
  isCustomCssDisabledByQuery,
  type CustomCSSRecord,
} from "@/lib/custom-css.js";

/**
 * Settings → Branding → Custom CSS panel (Tier 3 only).
 *
 * Saves a CSS snippet to IndexedDB and re-applies the global <style id="agencylens-custom-css">
 * on save / toggle / delete. Validator surfaces foot-gun rules before they
 * lock the buyer out of the UI.
 *
 * Recovery: `?nocss=1` in the URL disables injection just for that session.
 * Documented in failure-mode article #11.
 */
const PLACEHOLDER = `/* Example: tighten the agency-brand color */
.agencylens-customer-page h1 {
  color: #0f172a;
  letter-spacing: -0.02em;
}

/* Hide the AgencyLens default footer note on client-facing pages */
.agencylens-customer-page .agency-footer-text {
  display: none;
}
`;

export function CustomCssPanel({
  onError,
}: {
  onError: (msg: string | undefined) => void;
}) {
  const session = getSession();
  const tier = session?.payload.tier;
  const tierIsThree = tier === "tier-3";

  const [record, setRecord] = useState<CustomCSSRecord | undefined>();
  const [draft, setDraft] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState<string | undefined>();
  const [validation, setValidation] = useState<ReturnType<typeof validate> | null>(null);
  const queryDisabled = isCustomCssDisabledByQuery();

  useEffect(() => {
    (async () => {
      const r = await loadCustomCss();
      setRecord(r);
      if (r) {
        setDraft(r.css);
        setEnabled(r.enabled);
      }
    })().catch((e) => onError(String(e)));
  }, [onError]);

  function onValidate() {
    setValidation(validate(draft));
  }

  async function onSave() {
    onError(undefined);
    const v = validate(draft);
    setValidation(v);
    if (!v.ok) {
      onError("Fix the validation errors before saving.");
      return;
    }
    setBusy("Saving custom CSS…");
    try {
      const r = await saveCustomCss(draft, enabled);
      setRecord(r);
      await applyCustomCss(r);
    } catch (e) {
      onError(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onToggleEnabled() {
    if (!record) return;
    const next = !enabled;
    setEnabled(next);
    setBusy(next ? "Enabling…" : "Disabling…");
    try {
      const r = await saveCustomCss(record.css, next);
      setRecord(r);
      await applyCustomCss(r);
    } catch (e) {
      onError(`Toggle failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onReset() {
    if (!window.confirm("Remove your custom CSS? You can re-enter it later.")) {
      return;
    }
    onError(undefined);
    setBusy("Resetting…");
    try {
      await deleteCustomCss();
      setRecord(undefined);
      setDraft("");
      setEnabled(true);
      setValidation(null);
      await applyCustomCss(undefined);
    } catch (e) {
      onError(`Reset failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="mb-2 text-lg font-medium">Custom CSS</h2>
      <p className="mb-4 text-sm text-slate-600">
        Tier 3 lets you override the default agency-brand styles with your own
        CSS. Saves locally; mirrors to your Drive backup. If a rule locks you
        out of the dashboard, visit{" "}
        <code className="rounded bg-slate-100 px-1.5 py-0.5">/?nocss=1</code> to
        bypass injection just for that session.
      </p>

      {!tierIsThree && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Custom CSS is a <strong>Tier 3</strong> feature. Your current tier is{" "}
          <strong>{tier ?? "unknown"}</strong>.
        </div>
      )}

      {tierIsThree && (
        <>
          {queryDisabled && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <strong>?nocss=1</strong> is active for this session — your saved
              CSS is bypassed. Remove the query parameter to re-enable.
            </div>
          )}

          <label className="mb-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={() => void onToggleEnabled()}
              disabled={busy !== undefined || !record}
            />
            <span>
              Inject custom CSS on dashboard load
              {record && !enabled && (
                <span className="ml-2 text-xs text-slate-500">(saved but inactive)</span>
              )}
            </span>
          </label>

          <textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setValidation(null);
            }}
            placeholder={PLACEHOLDER}
            rows={14}
            spellCheck={false}
            className="w-full rounded-md border border-slate-200 px-3 py-2 font-mono text-xs"
            disabled={busy !== undefined}
          />

          {validation && (
            <div className="mt-2 space-y-1">
              {validation.errors.map((m, i) => (
                <div key={`e-${i}`} className="rounded border border-red-200 bg-red-50 px-3 py-1 text-xs text-red-900">
                  ✗ {m}
                </div>
              ))}
              {validation.warnings.map((m, i) => (
                <div key={`w-${i}`} className="rounded border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-900">
                  ⚠ {m}
                </div>
              ))}
              {validation.ok && validation.warnings.length === 0 && (
                <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-900">
                  ✓ No validation issues found.
                </div>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={onValidate} variant="secondary" disabled={busy !== undefined}>
              Validate
            </Button>
            <Button onClick={onSave} disabled={busy !== undefined}>
              Save
            </Button>
            {record && (
              <Button onClick={onReset} variant="ghost" disabled={busy !== undefined}>
                Reset to default
              </Button>
            )}
            {busy && <span className="self-center text-sm text-slate-500">{busy}</span>}
            {record && (
              <span className="self-center text-xs text-slate-500">
                Last saved {new Date(record.updatedAt).toLocaleString()}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
